import { defineComponent, h } from 'vue';
import { QBtn, QDialog } from 'quasar';
import utils from '../../../Shared/utils.jsx';
import BaseComponentMixin from './BaseComponentMixin.jsx';
import ControlPicker from './controlPicker.jsx';
import '../css/HtmlTable.css';

const TableCellDsgn = defineComponent({
    name: 'table-cell-dsgn',
    components: { QBtn },
    props: {
        root: { type: Object, default: null },
        schema: { type: Object, default: null },
        model: { type: Object, default: null },
        columnDef: { type: Object, default: null },
        selected$: { type: Boolean, default: false },
        firstrow: { type: Boolean, default: false },
        addControl: { type: Function, required: true },
    },
    computed: {
        Root() {
            return this.root;
        },
        selectedmodel$() {
            return this.Root?.SelectedModel && this.Root.SelectedModel === this.columnDef;
        },
    },
    methods: {
        addContent(e, content) {
            this.addControl(e, content);
        },
        addContentDef(e) {
            if (!this.columnDef.ContentDefs)
                this.columnDef.ContentDefs = [];

            this.columnDef.ContentDefs.push({
                $typeSchema: '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_Grid_ContentDef',
                $objectId: utils.generateUUID(),
                Controls: [],
            });
            e.cancelBubble = true;
            e.stopPropagation();
        },
    },
    render() {
        const cdef = this.columnDef;
        if (!cdef) return null;

        try {
            if (cdef.ContentDefs && cdef.ContentDefs.length >= 0) {
                const elements = [];
                for (let i = 0; i < cdef.ContentDefs.length; i++) {
                    const content = cdef.ContentDefs[i];
                    if (!content.Controls) continue;

                    for (let j = 0; j < content.Controls.length; j++) {
                        const control = content.Controls[j];

                        const DynamicControl = utils.getDesignerComponent(control, null);
                        if (!DynamicControl) {
                            elements.push(<span key={`unk_${j}`}>Control type of {control.ControlType} is unknown</span>);
                            continue;
                        }

                        if (!control.$objectId) control.$objectId = utils.generateUUID();

                        elements.push(
                            <DynamicControl
                                key={control.$objectId}
                                type={control.ControlType}
                                name={control.ControlData?.Name}
                                root={this.root}
                                controlData={control.ControlData}
                                designmodel={control}
                                rowData={this.model}
                            />
                        );
                    }

                    if (this.firstrow && this.selected$ && this.selectedmodel$) {
                        elements.push(
                            <div class="property-grid" key={`add-${i}`}>
                                {utils.tt(
                                    `Add control to column def #${i + 1}`,
                                    <QBtn flat round dense icon="mdi-plus-circle" color="blue-8" style={{ zIndex: 2 }} onClick={(e) => this.addContent(e, content.Controls)} />,
                                    'right'
                                )}
                            </div>
                        );
                    }
                }

                if (this.firstrow && this.selected$ && this.selectedmodel$) {
                    elements.push(
                        <div class="property-grid" key="add-cdef">
                            {utils.tt(
                                'Add column def',
                                <QBtn flat round dense icon="mdi-plus-circle" color="pink-9" style={{ zIndex: 2 }} onClick={(e) => this.addContentDef(e)} />,
                                'right'
                            )}
                        </div>
                    );
                }

                return (
                    <td style={{ borderColor: '#e0e0e0', borderWidth: '1px', overflow: 'hidden', maxWidth: '1px' }}>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                            }}>
                            {elements}
                        </div>
                    </td>
                );
            }
            else if (cdef.Field && cdef.Field in this.model) {
                const cell = this.model[cdef.Field];
                let text;
                if (cell === null || typeof cell === 'undefined')
                    text = '';
                else if (typeof cell === 'string')
                    text = cell;
                else if (typeof cell === 'boolean')
                    text = cell ? 'true' : 'false';
                else if (typeof cell === 'number')
                    text = String(cell);
                else
                    text = JSON.stringify(cell);

                let addcdef;
                if (this.firstrow && this.selected$ && this.selectedmodel$) {
                    addcdef = (
                        <div class="property-grid">
                            {utils.tt(
                                'Add column def',
                                <QBtn flat round dense icon="mdi-plus-circle" color="pink-9" style={{ zIndex: 2 }} onClick={(e) => this.addContentDef(e)} />,
                                'right'
                            )}
                        </div>
                    );
                }

                return (
                    <td
                        style={{
                            borderColor: '#e0e0e0',
                            borderWidth: '1px',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '1px',
                        }}>
                        <span>
                            {text} {addcdef}
                        </span>
                    </td>
                );
            }
            else {
                let addcdef;
                if (this.firstrow && this.selected$ && this.selectedmodel$) {
                    addcdef = (
                        <div class="property-grid">
                            {utils.tt(
                                'Add column def',
                                <QBtn flat round dense icon="mdi-plus-circle" color="pink-9" style={{ zIndex: 2 }} onClick={(e) => this.addContentDef(e)} />,
                                'right'
                            )}
                        </div>
                    );
                }

                return (
                    <td
                        style={{
                            borderColor: '#e0e0e0',
                            borderWidth: '1px',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            maxWidth: '1px',
                        }}>
                        {addcdef}
                    </td>
                );
            }
        }
        catch (e) {
            return (
                <td>
                    <span title={String(e)}>error</span>
                </td>
            );
        }
    },
});

const TableRowDsgn = defineComponent({
    name: 'table-row-dsgn',
    components: { TableCellDsgn },
    props: {
        name: { type: String, default: '' },
        root: { type: Object, default: null },
        schema: { type: Object, default: null },
        model: { type: Object, default: null },
        columnDefs: { type: Array, default: null },
        even: { type: Boolean, default: false },
        selected$: { type: Boolean, default: false },
        firstrow: { type: Boolean, default: false },
        addControl: { type: Function, required: true },
    },
    render() {
        if (!this.model) return null;

        const columns = [];

        if (this.columnDefs && this.columnDefs.length > 0) {
            for (let i = 0; i < this.columnDefs.length; i++) {
                const cdef = this.columnDefs[i];

                columns.push(
                    <TableCellDsgn
                        key={i}
                        selected$={this.selected$}
                        firstrow={this.firstrow}
                        addControl={this.addControl}
                        root={this.root}
                        schema={this.schema}
                        columnDef={cdef}
                        model={this.model}
                    />
                );
            }
        }
        else {
            for (const key in this.model) {
                const cdef = {
                    Field: key,
                };
                columns.push(
                    <TableCellDsgn
                        key={key}
                        selected$={this.selected$}
                        firstrow={this.firstrow}
                        addControl={this.addControl}
                        root={this.root}
                        schema={this.schema}
                        columnDef={cdef}
                        model={this.model}
                    />
                );
            }
        }

        const style = {
            backgroundColor: this.even ? '#f0f0f0' : 'white',
        };

        return <tr style={style}>{columns}</tr>;
    },
});

export default defineComponent({
    name: 'html-table-dsgn',
    mixins: [BaseComponentMixin],
    components: {
        TableRowDsgn,
        QDialog,
        ControlPicker,
    },
    data() {
        return {
            displayexpr: null,
            modelurl: null,
            modelvalue: null,
            model: null,
        };
    },
    mounted() {
        this.Refresh();
    },
    computed: {
        selected$() {
            return this.Root?.SelectedNode && this.Root.SelectedNode === this;
        },
    },
    methods: {
        handleClick(e) {
            this.Root.SelectNode(this);
            e.cancelBubble = true;
            e.stopPropagation();
            e.preventDefault();
        },
        selectColumn(e, index) {
            this.Root.SelectNode(this, this.controlData.ColumnDefs[index]);
            e.cancelBubble = true;
            e.stopPropagation();
        },
        deleteColumn(e, index) {
            this.controlData.ColumnDefs.splice(index, 1);
            e.cancelBubble = true;
            e.stopPropagation();
        },
        addColumn(e) {
            if (!this.controlData.ColumnDefs)
                this.controlData.ColumnDefs = [];

            this.controlData.ColumnDefs.push({
                $typeSchema: '/schema/public/Platform.Schema.DynamicControls.v1/DynamicControl_Grid_ColumnDef',
                Align: 'Left',
                DisplayName: '',
                Field: 'field' + (this.controlData.ColumnDefs.length + 1),
                Sortable: true,
                Visible: true,
                Width: 'Auto',
            });
            this.Root.SelectNode(this, this.controlData.ColumnDefs[this.controlData.ColumnDefs.length - 1]);
            e.cancelBubble = true;
            e.stopPropagation();
        },
        async Refresh() {
            if (this.controlData.SourceType == 'Url' && this.controlData.SourceUrl) {
                try {
                    this.model = await utils.api.get(this.controlData.SourceUrl);
                }
                catch (e) {
                    utils.warn('Design HtmlTable SourceUrl ' + this.controlData.SourceUrl + ' failed to load: ' + e);
                    this.model = [{}];
                }
            }
            else if (this.controlData.SourceType == 'Raw' && this.controlData.SourceRaw && Array.isArray(this.controlData.SourceRaw)) {
                this.model = this.controlData.SourceRaw;
            }
            else this.model = [{}];
        },
    },
    props: {
        schema: { type: Object, default: null },
    },
    render() {
        const headerRow = [];
        const bodyRows = [];

        if (this.controlData.ColumnDefs && this.controlData.ColumnDefs.length > 0) {
            for (let i = 0; i < this.controlData.ColumnDefs.length; i++) {
                const cdef = this.controlData.ColumnDefs[i];
                const headerLabel = cdef.Header != null && cdef.Header !== '' ? cdef.Header : cdef.DisplayName || cdef.Field || cdef.Name;

                headerRow.push(
                    <th key={i} style={{ minWidth: '150px' }}>
                        <span title="Edit Column" style={{ cursor: 'pointer' }} onClick={(e) => this.selectColumn(e, i)}>
                            {headerLabel}
                        </span>
                        <span
                            class="design-options"
                            title="Delete Column"
                            style={{
                                cursor: 'pointer',
                                fontSize: 'smaller',
                                display: this.selected$ ? 'inline' : 'none',
                                color: 'blue',
                            }}
                            onClick={(e) => this.deleteColumn(e, i)}>
                            <i class="mdi mdi-close" />
                        </span>
                    </th>
                );
            }
        }
        else if (this.model && this.model.length > 0) {
            const sample = this.model[0];
            for (const head in sample) {
                headerRow.push(<th key={head}>{head}</th>);
            }
        }

        if (!this.model)
            bodyRows.push(
                <tr key="loading">
                    <td>Loading...</td>
                </tr>
            );
        else {
            let c = this.model.length;
            if (c > 10) c = 10;
            for (let i = 0; i < c; i++) {
                const row = this.model[i];
                if (!row.$objectId) row.$objectId = utils.generateUUID();

                bodyRows.push(
                    <TableRowDsgn
                        key={row.$objectId}
                        selected$={this.selected$}
                        firstrow={i === 0}
                        addControl={this.addControl}
                        root={this.root}
                        schema={this.schema}
                        model={row}
                        columnDefs={this.controlData.ColumnDefs}
                        even={i % 2 === 0}
                    />
                );
            }
        }

        return (
            <div
                class={{
                    'c-HtmlTable': true,
                    [`c-name-${this.name || 'unnamed'}`]: true,
                    'designer-form': true,
                    selected: this.selected$,
                }}
                style={{
                    borderStyle: 'solid',
                    borderWidth: '1px',
                    position: 'relative',
                    padding: '5px',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    ...utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent),
                }}
                onClick={(e) => this.handleClick(e)}>
                <div class={{ 'designer-container-hovering': true, 'designer-container-selected-clickthrough': this.selected$ }} />

                <div
                    key="add-col"
                    class="design-options"
                    title="Add Column"
                    onClick={(e) => this.addColumn(e)}
                    style={{ top: '1px', right: '1px', display: this.selected$ ? 'block' : 'none' }}>
                    <i class="mdi mdi-plus-circle" />
                </div>

                <table class="c-HtmlTable__table" style={{ width: '100%' }}>
                    {headerRow.length > 0 ? (
                        <thead>
                            <tr>{headerRow}</tr>
                        </thead>
                    ) : null}
                    <tbody>{bodyRows}</tbody>
                </table>

                {this.generatePicker(h)}
            </div>
        );
    },
});
